/**
 * <latest-videos> — fills the placeholder cards rendered by
 * sections/latest-videos.liquid with the channel's newest long-form uploads.
 *
 * The feed comes from the Vercel proxy in the section's "Feed URL" setting,
 * not from YouTube directly: googleapis.com sends no CORS header, and an API
 * key in theme JS is a key anyone can copy. See
 * channel199-youtube-proxy/README.md.
 *
 * Liquid has already rendered one placeholder <li> per requested video, so
 * this only ever swaps content into slots that exist. Nothing is appended and
 * nothing reflows, which is what keeps the section from shifting the page when
 * the fetch lands.
 */

class LatestVideos extends HTMLElement {
  connectedCallback() {
    /* connectedCallback fires again if the element is moved in the DOM. A
       section reload in the theme editor replaces the element outright, so
       that still re-fetches — which is what we want. */
    if (this.initialised) return;
    this.initialised = true;

    this.designMode = this.dataset.designMode === 'true';
    this.showDuration = this.dataset.showDuration === 'true';
    this.showShare = this.dataset.showShare === 'true';
    this.limit = parseInt(this.dataset.limit, 10) || 4;
    /* 0 disables the browser cache entirely. The theme editor always bypasses
       it, or a merchant changing settings would keep seeing a stale row. */
    this.cacheMs = this.designMode
      ? 0
      : (parseFloat(this.dataset.cacheHours) || 0) * 60 * 60 * 1000;

    this.slots = Array.from(this.querySelectorAll('[data-latest-video-slot]'));
    this.notice = this.querySelector('[data-latest-videos-notice]');
    this.status = this.querySelector('[data-latest-videos-status]');

    this.onDocumentClick = this.handleDocumentClick.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    /* An open menu is positioned against the button's viewport coordinates, so
       any scroll or resize invalidates it. Closing is the honest response —
       tracking it would mean repositioning on every scroll frame. Capture
       catches the carousel's own horizontal scrolling too. */
    this.onViewportChange = () => this.closeMenus();
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('scroll', this.onViewportChange, { passive: true, capture: true });
    window.addEventListener('resize', this.onViewportChange);
    this.addEventListener('click', this.handleClick.bind(this));

    this.load();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('scroll', this.onViewportChange, { capture: true });
    window.removeEventListener('resize', this.onViewportChange);
  }

  async load() {
    const feedUrl = (this.dataset.feedUrl || '').trim();

    if (!feedUrl) {
      this.fallback(
        'Add your feed URL in this section’s settings — the /api/videos endpoint of your Vercel proxy.'
      );
      return;
    }

    let url;
    try {
      url = new URL(feedUrl, window.location.origin);
      url.searchParams.set('limit', String(this.limit));
    } catch {
      this.fallback(`“${feedUrl}” is not a valid feed URL.`);
      return;
    }

    /* Keyed on the full request, so changing the video count or pointing at a
       different proxy starts a fresh entry instead of serving the old one. */
    this.cacheKey = `latest-videos:v1:${url.toString()}`;

    /* Paint from the last known good list first — even if it is past its age,
       stale cards on screen beat a row of shimmering placeholders. */
    const cached = this.readCache();
    if (cached) {
      this.render(cached.videos);
      const age = Date.now() - cached.savedAt;
      /* Still fresh: no network request at all this page view. */
      if (age >= 0 && age < this.cacheMs) return;
    }

    try {
      const videos = await this.fetchFeed(url);
      this.writeCache(videos);

      /* Nothing new since last visit is the common case, and re-rendering an
         identical row would flicker it for no reason. */
      if (!cached || !sameVideos(cached.videos, videos)) this.render(videos);
    } catch (error) {
      /* A background refresh that fails leaves the cached row exactly where it
         is. Only a cold start with nothing to show falls back. */
      if (cached) return;
      this.fallback(
        `Could not load the YouTube feed: ${error.message} If this is a CORS error, add this storefront’s origin to ALLOWED_ORIGINS on the Vercel project.`
      );
    }
  }

  async fetchFeed(url) {
    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json' },
    });
    const data = await response.json().catch(() => null);

    if (!response.ok || !data) {
      throw new Error(data?.error || `Feed responded with ${response.status}`);
    }

    const videos = Array.isArray(data.videos) ? data.videos.slice(0, this.limit) : [];
    if (!videos.length) throw new Error('The feed returned no videos.');
    return videos;
  }

  /**
   * Every localStorage call is wrapped: it throws outright in some private
   * browsing modes, and writes throw once the origin's storage is full.
   * A cache that cannot be read or written is not an error — just a slower
   * page view.
   */
  readCache() {
    if (!this.cacheMs) return null;
    try {
      const raw = window.localStorage.getItem(this.cacheKey);
      if (!raw) return null;

      const entry = JSON.parse(raw);
      if (!Array.isArray(entry?.videos) || !entry.videos.length) return null;
      if (typeof entry.savedAt !== 'number') return null;

      return entry;
    } catch {
      return null;
    }
  }

  writeCache(videos) {
    if (!this.cacheMs) return;
    try {
      window.localStorage.setItem(
        this.cacheKey,
        JSON.stringify({ savedAt: Date.now(), videos })
      );
    } catch {
      /* Storage full, or blocked. The section works without it. */
    }
  }

  render(videos) {
    /* Surplus slots are hidden rather than removed, so this can run a second
       time when a background refresh brings back a longer list. SliderComponent
       measures slides by clientWidth, so a hidden slot drops out of paging on
       its own. */
    this.slots.forEach((slot, index) => {
      const video = videos[index];
      if (!video) {
        slot.hidden = true;
        return;
      }
      slot.hidden = false;
      slot.replaceChildren(this.buildCard(video));
    });

    this.refreshSlider(videos.length);

    if (this.status) {
      this.status.textContent = `${videos.length} latest ${
        videos.length === 1 ? 'video' : 'videos'
      } loaded.`;
    }
  }

  /**
   * One anchor per card: the title carries the accessible name and an inset
   * ::after stretches it over the whole card. A second link around the
   * thumbnail would make every video announce twice.
   */
  buildCard(video) {
    const card = document.createElement('div');
    card.className = 'latest-video';

    const thumb = document.createElement('div');
    thumb.className = 'latest-video__thumb';

    if (video.thumbnail) {
      const img = document.createElement('img');
      img.src = video.thumbnail;
      /* Decorative: the title link next to it carries the accessible name. */
      img.alt = '';
      /* setAttribute rather than the IDL properties — `loading` and `decoding`
         do not reflect to the attribute in every engine, and the attribute is
         what the browser's preload scanner reads. */
      img.setAttribute('loading', 'lazy');
      img.setAttribute('decoding', 'async');
      /* Intrinsic size so the browser can reserve the box before the bytes
         arrive, even though the CSS aspect-ratio already does most of that. */
      if (video.thumbnailWidth) img.setAttribute('width', video.thumbnailWidth);
      if (video.thumbnailHeight) img.setAttribute('height', video.thumbnailHeight);
      thumb.appendChild(img);
    }

    if (this.showDuration && video.duration) {
      const duration = document.createElement('span');
      duration.className = 'latest-video__duration';
      duration.textContent = video.duration;
      thumb.appendChild(duration);
    }

    const meta = document.createElement('div');
    meta.className = 'latest-video__meta';

    const title = document.createElement('a');
    title.className = 'latest-video__title';
    title.href = video.url;
    title.target = '_blank';
    title.rel = 'noopener noreferrer';
    /* textContent, never innerHTML — the title is third-party data. */
    title.textContent = video.title;
    meta.appendChild(title);

    if (this.showShare) meta.appendChild(this.buildShareMenu(video));

    card.append(thumb, meta);
    return card;
  }

  buildShareMenu(video) {
    const wrapper = document.createElement('div');
    wrapper.className = 'latest-video__share';

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'latest-video__share-toggle';
    toggle.setAttribute('aria-haspopup', 'true');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', `Share options for ${video.title}`);
    toggle.dataset.shareToggle = '';
    for (let i = 0; i < 3; i += 1) toggle.appendChild(document.createElement('span'));

    const menu = document.createElement('ul');
    menu.className = 'latest-video__share-menu';
    menu.hidden = true;

    menu.appendChild(this.buildMenuItem('button', 'Copy link', { action: 'copy', url: video.url }));

    /* Only worth offering where the OS share sheet actually exists — mostly
       mobile. On desktop it would be a menu item that does nothing. */
    if (navigator.share) {
      menu.appendChild(
        this.buildMenuItem('button', 'Share…', {
          action: 'share',
          url: video.url,
          title: video.title,
        })
      );
    }

    const watch = document.createElement('a');
    watch.href = video.url;
    watch.target = '_blank';
    watch.rel = 'noopener noreferrer';
    watch.textContent = 'Watch on YouTube';
    const watchItem = document.createElement('li');
    watchItem.appendChild(watch);
    menu.appendChild(watchItem);

    wrapper.append(toggle, menu);
    return wrapper;
  }

  buildMenuItem(tag, label, dataset) {
    const item = document.createElement('li');
    const control = document.createElement(tag);
    control.type = 'button';
    control.textContent = label;
    Object.entries(dataset).forEach(([key, value]) => {
      control.dataset[key] = value;
    });
    item.appendChild(control);
    return item;
  }

  handleClick(event) {
    const toggle = event.target.closest('[data-share-toggle]');
    if (toggle) {
      event.preventDefault();
      this.toggleMenu(toggle);
      return;
    }

    const action = event.target.closest('[data-action]');
    if (!action) return;

    event.preventDefault();
    if (action.dataset.action === 'copy') this.copyLink(action);
    if (action.dataset.action === 'share') {
      navigator
        .share({ title: action.dataset.title, url: action.dataset.url })
        .catch(() => {
          /* The user dismissed the share sheet. Not an error. */
        });
      this.closeMenus();
    }
  }

  toggleMenu(toggle) {
    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
    this.closeMenus();
    if (isOpen) return;

    const menu = toggle.nextElementSibling;
    toggle.setAttribute('aria-expanded', 'true');
    /* Unhide before measuring — a hidden element has no box. */
    menu.hidden = false;
    this.positionMenu(toggle, menu);
  }

  /**
   * The menu is position: fixed to escape the carousel's scroll container, so
   * it has to be placed by hand. Right-aligned to the button, and opening
   * upward by default: the button sits at the bottom of a card with a whole
   * thumbnail above it and the section's bottom edge just below, so upward is
   * both where the room is and what YouTube's own card menus do. It drops
   * downward only when the top of the viewport is in the way.
   */
  positionMenu(toggle, menu) {
    const button = toggle.getBoundingClientRect();
    const { width, height } = menu.getBoundingClientRect();
    const edge = 8;
    const gap = 4;

    let top = button.top - height - gap;
    if (top < edge) {
      top = button.bottom + gap;
    }
    top = Math.max(edge, Math.min(top, window.innerHeight - height - edge));

    const left = Math.max(
      edge,
      Math.min(button.right - width, window.innerWidth - width - edge)
    );

    menu.style.top = `${top}px`;
    menu.style.left = `${left}px`;
  }

  closeMenus() {
    this.querySelectorAll('[data-share-toggle][aria-expanded="true"]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false');
      toggle.nextElementSibling.hidden = true;
    });
  }

  handleDocumentClick(event) {
    if (!this.contains(event.target)) this.closeMenus();
  }

  handleKeydown(event) {
    if (event.key !== 'Escape') return;
    const open = this.querySelector('[data-share-toggle][aria-expanded="true"]');
    if (!open) return;
    this.closeMenus();
    open.focus();
  }

  async copyLink(control) {
    const original = control.textContent;
    try {
      await navigator.clipboard.writeText(control.dataset.url);
      control.textContent = 'Link copied';
    } catch {
      control.textContent = 'Press ⌘C to copy';
    }
    window.setTimeout(() => {
      control.textContent = original;
      this.closeMenus();
    }, 1600);
  }

  /**
   * SliderComponent measured its pages against the placeholder cards. Now that
   * the real ones are in — and any surplus removed — it has to re-measure, or
   * the arrows and the counter will be wrong.
   */
  refreshSlider(count) {
    const slider = this.querySelector('slider-component');
    if (!slider) return;

    const total = slider.querySelector('.slider-counter--total');
    if (total) total.textContent = count;

    if (typeof slider.resetPages === 'function' && slider.nextButton) {
      slider.resetPages();
    }
  }

  /**
   * In the theme editor, say what went wrong so the merchant can fix it. On the
   * live storefront, take the whole section off the page — a row of empty grey
   * boxes is worse than no section at all.
   */
  fallback(message) {
    if (this.designMode && this.notice) {
      this.notice.textContent = message;
      this.notice.hidden = false;
      this.slots.forEach((slot) => slot.remove());
      const slider = this.querySelector('slider-component');
      if (slider) slider.hidden = true;
      return;
    }

    const section = this.closest('.shopify-section') || this;
    section.style.display = 'none';
  }
}

function sameVideos(a, b) {
  return a.length === b.length && a.every((video, i) => video.id === b[i].id);
}

customElements.define('latest-videos', LatestVideos);
